import { CalendarClock, CalendarDays, CheckCircle2, ChevronRight, Hourglass, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkDueFilter, WorkStatusFilter } from './WorkToolbar';

export interface WorkCounts {
  total: number;
  /** Of the total, how many were set in the calendar month showing. */
  thisMonth: number;
  completed: number;
  /** Everything not handed in and not yet past its date. */
  pending: number;
  overdue: number;
  dueThisWeek: number;
}

export interface WorkStatRowProps {
  counts: WorkCounts;
  /** Which view is in force, so the tile standing for it reads as pressed. */
  status: WorkStatusFilter;
  due: WorkDueFilter;
  onStatus: (status: WorkStatusFilter) => void;
  onDueThisWeek: () => void;
  /**
   * "Tasks" or "Assignments", capitalised — it leads the first tile's label.
   */
  noun: string;
}

/** `7` of `12` → `58%`. Nothing out of nothing is shown as a dash, not 0%. */
function share(part: number, whole: number): string {
  if (whole === 0) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

export function WorkStatRow({
  counts,
  status,
  due,
  onStatus,
  onDueThisWeek,
  noun,
}: WorkStatRowProps) {
  const isAllActive = status === 'ALL' && due === 'all';
  const isCompletedActive = status === 'COMPLETED';
  const isPendingActive = status === 'PENDING';
  const isOverdueActive = status === 'OVERDUE';
  const isDueThisWeekActive = due === 'week';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
      {/* Card 1: Total Assignments / Total Tasks (Purple Variant) */}
      <button
        type="button"
        onClick={() => onStatus('ALL')}
        className={cn(
          'group relative block overflow-hidden rounded-2xl sm:rounded-3xl border p-3.5 sm:p-5 text-left shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 cursor-pointer',
          'border-purple-200/90 dark:border-slate-800 bg-purple-50/90 dark:bg-slate-900',
          isAllActive && 'ring-1 ring-inset ring-purple-500/30 !border-purple-500 dark:!border-purple-400',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm sm:text-[14px] font-bold text-purple-800 dark:text-purple-400 leading-tight">
              Total {noun}
            </p>
            <p className="mt-0.5 text-2xl font-black text-purple-600 dark:text-purple-400 tracking-tight tabular-nums truncate">
              {counts.total}
            </p>
            {/* The big figure is the whole session's; this line is the part of
                it set in the current month, which is what "this month" means. */}
            <p className="mt-0.5 sm:mt-1 text-[12px] font-semibold text-purple-600/90 dark:text-slate-400">
              {counts.thisMonth} set this month
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#7c3aed' }}
            aria-hidden
          >
            <CalendarDays className="h-5 w-5" />
          </span>
        </div>
      </button>

      {/* Card 2: Completed (Emerald Variant) */}
      <button
        type="button"
        onClick={() => onStatus('COMPLETED')}
        className={cn(
          'group relative block overflow-hidden rounded-2xl sm:rounded-3xl border p-3.5 sm:p-5 text-left shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 cursor-pointer',
          'border-emerald-200/90 dark:border-slate-800 bg-emerald-50/90 dark:bg-slate-900',
          isCompletedActive && 'ring-1 ring-inset ring-emerald-500/30 !border-emerald-500 dark:!border-emerald-400',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm sm:text-[14px] font-bold text-emerald-800 dark:text-emerald-400 leading-tight">
              Completed
            </p>
            <p className="mt-0.5 text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight tabular-nums truncate">
              {counts.completed}
            </p>
            <p className="mt-0.5 sm:mt-1 text-[12px] font-semibold text-emerald-700/90 dark:text-slate-400">
              {share(counts.completed, counts.total)}
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#059669' }}
            aria-hidden
          >
            <CheckCircle2 className="h-5 w-5" />
          </span>
        </div>
      </button>

      {/* Card 3: Pending (Amber Variant) */}
      <button
        type="button"
        onClick={() => onStatus('PENDING')}
        className={cn(
          'group relative block overflow-hidden rounded-2xl sm:rounded-3xl border p-3.5 sm:p-5 text-left shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 cursor-pointer',
          'border-amber-200/90 dark:border-slate-800 bg-amber-50/90 dark:bg-slate-900',
          isPendingActive && 'ring-1 ring-inset ring-amber-500/30 !border-amber-500 dark:!border-amber-400',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm sm:text-[14px] font-bold text-amber-800 dark:text-amber-400 leading-tight">
              Pending
            </p>
            <p className="mt-0.5 text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight tabular-nums truncate">
              {counts.pending}
            </p>
            <p className="mt-0.5 sm:mt-1 text-[12px] font-semibold text-amber-700/90 dark:text-slate-400">
              {share(counts.pending, counts.total)}
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#d97706' }}
            aria-hidden
          >
            <Hourglass className="h-5 w-5" />
          </span>
        </div>
      </button>

      {/* Card 4: Overdue (Rose Variant) */}
      <button
        type="button"
        onClick={() => onStatus('OVERDUE')}
        className={cn(
          'group relative block overflow-hidden rounded-2xl sm:rounded-3xl border p-3.5 sm:p-5 text-left shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 cursor-pointer',
          'border-rose-200/90 dark:border-slate-800 bg-rose-50/90 dark:bg-slate-900',
          isOverdueActive && 'ring-1 ring-inset ring-rose-500/30 !border-rose-500 dark:!border-rose-400',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm sm:text-[14px] font-bold text-rose-800 dark:text-rose-400 leading-tight">
              Overdue
            </p>
            <p className="mt-0.5 text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight tabular-nums truncate">
              {counts.overdue}
            </p>
            <p className="mt-0.5 sm:mt-1 text-[12px] font-semibold text-rose-600/90 dark:text-slate-400">
              {share(counts.overdue, counts.total)}
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#e11d48' }}
            aria-hidden
          >
            <TriangleAlert className="h-5 w-5" />
          </span>
        </div>
      </button>

      {/* Card 5: Due this week (Sky Variant) */}
      <button
        type="button"
        onClick={onDueThisWeek}
        className={cn(
          'group relative block overflow-hidden rounded-2xl sm:rounded-3xl border p-3.5 sm:p-5 text-left shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 cursor-pointer sm:col-span-2 md:col-span-1',
          'border-sky-200/90 dark:border-slate-800 bg-sky-50/90 dark:bg-slate-900',
          isDueThisWeekActive && 'ring-1 ring-inset ring-sky-500/30 !border-sky-500 dark:!border-sky-400',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-[14px] font-bold text-sky-800 dark:text-sky-400 leading-tight">
              Due This Week
            </p>
            <p className="mt-0.5 text-2xl font-black text-sky-600 dark:text-sky-400 tracking-tight tabular-nums truncate">
              {counts.dueThisWeek}
            </p>
            <p className="mt-0.5 sm:mt-1 text-[12px] font-semibold text-sky-600 dark:text-sky-400 flex items-center gap-0.5 group-hover:underline">
              <span>View Details</span>
              <ChevronRight className="h-3 w-3" />
            </p>
          </div>
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
            style={{ backgroundColor: '#0284c7' }}
            aria-hidden
          >
            <CalendarClock className="h-5 w-5" />
          </span>
        </div>
      </button>
    </div>
  );
}
